/**
 * Trang xem trước kho giao diện.
 *
 * Cách trình bày ở đây là cả một luận điểm: bốn giao diện dựng **sống, cùng lúc, cạnh
 * nhau** chứ không phải bốn ảnh chụp màn hình. Mỗi thẻ tự đặt trọn bộ biến CSS của
 * giao diện nó đại diện lên chính nó, nên phần thu nhỏ bên trong là thành phần thật
 * đang chạy dưới bộ token thật — thấy được cùng lúc bốn nền, bốn bộ chữ, bốn độ bo
 * góc, bốn cách vẽ mục đang chọn.
 *
 * Trang này cũng là chỗ chứng minh kho mở rộng được: thêm một tệp vào `src/themes/`
 * là thẻ thứ năm xuất hiện ở đây, không phải sửa gì trong tệp này.
 */

import { useEffect, type CSSProperties, type JSX } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BrandMark } from '../components/BrandMark';
import { useTheme } from '../themes/ThemeProvider';
import { THEMES, THEME_QUERY_PARAM, type Theme } from '../themes';
import styles from './ThemeGallery.module.css';

/** Biến CSS của một giao diện, đóng gói thành `style` để đặt lên đúng một phần tử. */
function tokenStyle(theme: Theme): CSSProperties {
  const style: Record<string, string> = {};
  for (const [key, value] of Object.entries(theme.tokens)) style[`--${key}`] = value;
  // Phần thu nhỏ luôn nằm trong khung hẹp, nên bỏ qua bề rộng nội dung của giao diện.
  delete style['--content-max'];
  return style as CSSProperties;
}

const MINI_NAV = ['Thể thao', 'Xổ số', 'Lô đề', 'Khuyến mại'];

export function ThemeGalleryPage(): JSX.Element {
  const { theme: active, brand, locked, setTheme } = useTheme();
  const navigate = useNavigate();

  // Nạp sẵn bộ chữ của MỌI giao diện: trang này hiển thị cả bốn cùng lúc, nên chờ
  // từng bộ tải theo lượt sẽ làm các thẻ nhảy chữ lần lượt trước mắt người xem.
  useEffect(() => {
    for (const t of THEMES) {
      const id = `theme-font-${t.id}`;
      if (document.getElementById(id)) continue;
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href = t.fonts.href;
      document.head.appendChild(link);
    }
  }, []);

  function choose(theme: Theme): void {
    setTheme(theme.id);
    navigate('/');
  }

  return (
    <div className={styles.page}>
      <header className={styles.masthead}>
        <p className={styles.eyebrow}>Kho giao diện</p>
        <h1 className={styles.title}>Cùng một hệ thống, khác nhau tới mức không nhận ra</h1>
        <p className={styles.lede}>
          Bốn giao diện dưới đây chạy trên cùng một bộ mã, cùng một máy chủ, cùng một cơ sở
          dữ liệu. Khác nhau ở nền sáng hay tối, bộ chữ, độ bo góc, chỗ đặt thanh điều hướng
          và cách vẽ mục đang chọn — năm trục đủ để hai website không còn dấu vết họ hàng.
          Mỗi nhà khai thác nhận một tên miền chính và một tên miền dự phòng cùng trỏ về
          đúng giao diện của thương hiệu đó.
        </p>
      </header>

      {locked && (
        <p className={styles.lockedNote} role="status">
          Tên miền này đã gắn thương hiệu <strong>{brand?.name ?? active.brandName}</strong>, nên
          giao diện được khóa. Trang xem trước chỉ đổi được trên tên miền trình diễn của nền tảng.
        </p>
      )}

      <div className={styles.grid}>
        {THEMES.map((theme) => {
          const isActive = theme.id === active.id;
          // Tên miền chỉ hiện cho giao diện mà thương hiệu của tên miền này đang dùng —
          // các giao diện khác trong kho chưa gắn với tên miền nào để mà kể ra.
          const hosts = brand && brand.theme_id === theme.id && brand.domain ? [brand.domain] : [];

          return (
            <article
              key={theme.id}
              className={[styles.card, isActive ? styles.cardActive : ''].filter(Boolean).join(' ')}
              style={tokenStyle(theme)}
              data-nav={theme.navStyle}
            >
              {/* ---- Phần thu nhỏ: dựng bằng chính token của giao diện này ---- */}
              <div className={styles.preview}>
                <div className={styles.previewHeader}>
                  <span className={styles.previewBrand}>
                    <BrandMark mark={theme.mark} size={20} />
                    <span className={styles.previewBrandName}>{theme.brandName}</span>
                  </span>
                  <span className={styles.previewBalance}>
                    <span className={styles.previewBalanceLabel}>Số dư</span>
                    <span className={`numeric ${styles.previewBalanceValue}`}>2.450.000</span>
                  </span>
                </div>

                <div
                  className={[
                    styles.previewBody,
                    theme.layout === 'sidebar' ? styles.previewBodySidebar : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <nav
                    className={[
                      styles.previewNav,
                      theme.layout === 'sidebar' ? styles.previewNavRail : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {MINI_NAV.map((label, i) => (
                      <span
                        key={label}
                        className={[styles.previewNavItem, i === 2 ? styles.previewNavItemActive : '']
                          .filter(Boolean)
                          .join(' ')}
                      >
                        {label}
                      </span>
                    ))}
                  </nav>

                  <div className={styles.previewPanel}>
                    <p className={styles.previewPanelTitle}>Miền Bắc · 18:15</p>
                    <div className={styles.previewNumbers}>
                      {['27', '90', '45', '08'].map((n) => (
                        <span key={n} className={`numeric ${styles.previewNumber}`}>{n}</span>
                      ))}
                    </div>
                    <div className={styles.previewActions}>
                      <span className={styles.previewButton}>Đặt cược</span>
                      <span className={styles.previewButtonGhost}>Xem bảng</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ---- Phần mô tả: vẫn nằm trong bộ token của thẻ, nên cả thẻ là một
                     thế giới trọn vẹn chứ không phải ảnh dán trên nền chung ---- */}
              <div className={styles.meta}>
                <h2 className={styles.cardName}>{theme.brandName}</h2>
                <p className={styles.cardTagline}>{theme.tagline}</p>

                <dl className={styles.specs}>
                  <div className={styles.spec}>
                    <dt>Nền</dt>
                    <dd>{theme.scheme === 'light' ? 'Sáng' : 'Tối'}</dd>
                  </div>
                  <div className={styles.spec}>
                    <dt>Bố cục</dt>
                    <dd>
                      {theme.layout === 'sidebar' ? 'Cột trái' : theme.layout === 'wide' ? 'Tràn rộng' : 'Thanh trên'}
                    </dd>
                  </div>
                  <div className={styles.spec}>
                    <dt>Chữ tiêu đề</dt>
                    <dd>{theme.fonts.display.replace(/'/g, '')}</dd>
                  </div>
                </dl>

                {hosts.length > 0 && (
                  <ul className={styles.hosts}>
                    {hosts.map((h, i) => (
                      <li key={h} className={styles.host}>
                        <span className={styles.hostRole}>{i === 0 ? 'Chính' : 'Dự phòng'}</span>
                        <span className={`numeric ${styles.hostName}`}>{h}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <div className={styles.cardActions}>
                  <button
                    type="button"
                    className={styles.apply}
                    onClick={() => choose(theme)}
                    disabled={locked || isActive}
                  >
                    {isActive ? 'Đang dùng' : 'Dùng giao diện này'}
                  </button>
                  <Link
                    className={styles.previewLink}
                    to={`/?${THEME_QUERY_PARAM}=${theme.id}`}
                  >
                    Mở thử
                  </Link>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <footer className={styles.footnote}>
        <h2 className={styles.footnoteTitle}>Thêm một giao diện nữa</h2>
        <p>
          Tạo một tệp trong <code>web/src/themes/</code> theo mẫu <code>kim-long.ts</code>,
          rồi thêm đúng một dòng vào mảng <code>THEMES</code> của{' '}
          <code>web/src/themes/index.ts</code>. Không thành phần nào phải sửa, không tệp CSS
          nào phải sửa, và giao diện mới xuất hiện ngay ở trang này.
        </p>
        <p>
          Gắn tên miền: thêm một hàng vào bảng <code>brand</code> với tên miền và mã giao
          diện, rồi trỏ tên miền về dự án. Ánh xạ nằm ở máy chủ (<code>GET /v1/brand</code>)
          chứ không trong mã, nên thêm thương hiệu không phải triển khai lại. Một bản dựng
          phục vụ mọi tên miền.
        </p>
      </footer>
    </div>
  );
}
